import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import {
  FileText,
  FolderOpen,
  Target,
  Coins,
  ArrowRight,
  Plus,
  Search,
  BarChart3,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChartSkeleton, DashboardChartsSkeleton } from '@/components/ui/page-skeletons';
import { StatCard } from '@/features/dashboard/components/StatCard';
import { RecentEvals } from '@/features/dashboard/components/RecentEvals';
import { CreditWarning } from '@/features/dashboard/components/CreditWarning';
import { AuditTrail } from '@/features/dashboard/components/AuditTrail';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { useCredits } from '@/features/dashboard/hooks/useCredits';

interface DashboardStats {
  totalSources: number;
  totalFolders: number;
  totalEvaluations: number;
  completedEvals: number;
  avgScore: number;
}

interface EvalByDay {
  date: string;
  count: number;
}

interface ScoreDistribution {
  range: string;
  count: number;
}

function useDashboardStats() {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: ['dashboard-stats', user?.id],
    queryFn: async (): Promise<DashboardStats> => {
      if (!user) {
        return { totalSources: 0, totalFolders: 0, totalEvaluations: 0, completedEvals: 0, avgScore: 0 };
      }

      const [sourcesRes, foldersRes, evalsRes] = await Promise.all([
        supabase
          .from('sources')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id),
        supabase
          .from('folders')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id),
        supabase
          .from('evaluations')
          .select('id, status, score')
          .eq('user_id', user.id),
      ]);

      const evaluations = evalsRes.data ?? [];
      const completed = evaluations.filter((e) => e.status === 'completed');
      const scores = completed
        .map((e) => e.score)
        .filter((s): s is number => s !== null);
      const avgScore = scores.length > 0
        ? scores.reduce((a, b) => a + b, 0) / scores.length
        : 0;

      return {
        totalSources: sourcesRes.count ?? 0,
        totalFolders: foldersRes.count ?? 0,
        totalEvaluations: evaluations.length,
        completedEvals: completed.length,
        avgScore,
      };
    },
    enabled: !!user,
  });
}

function useEvalActivity() {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: ['eval-activity', user?.id],
    queryFn: async (): Promise<EvalByDay[]> => {
      if (!user) return [];

      const thirtyDaysAgo = subDays(new Date(), 30).toISOString();
      const { data, error } = await supabase
        .from('evaluations')
        .select('created_at')
        .eq('user_id', user.id)
        .gte('created_at', thirtyDaysAgo)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const byDay = new Map<string, number>();
      for (let i = 29; i >= 0; i--) {
        const date = format(subDays(new Date(), i), 'MMM d');
        byDay.set(date, 0);
      }

      for (const row of data ?? []) {
        const date = format(new Date(row.created_at), 'MMM d');
        byDay.set(date, (byDay.get(date) ?? 0) + 1);
      }

      return Array.from(byDay.entries()).map(([date, count]) => ({ date, count }));
    },
    enabled: !!user,
  });
}

function useScoreDistribution() {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: ['score-distribution', user?.id],
    queryFn: async (): Promise<ScoreDistribution[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('evaluations')
        .select('score')
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .not('score', 'is', null);

      if (error) throw error;

      const ranges = [
        { range: '0-20', min: 0, max: 20, count: 0 },
        { range: '21-40', min: 21, max: 40, count: 0 },
        { range: '41-60', min: 41, max: 60, count: 0 },
        { range: '61-80', min: 61, max: 80, count: 0 },
        { range: '81-100', min: 81, max: 100, count: 0 },
      ];

      for (const row of data ?? []) {
        if (row.score === null) continue;
        const bucket = ranges.find((r) => row.score >= r.min && row.score <= r.max);
        if (bucket) bucket.count++;
      }

      return ranges.map(({ range, count }) => ({ range, count }));
    },
    enabled: !!user,
  });
}

export function Dashboard() {
  const profile = useAuthStore((s) => s.profile);
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: credits, isLoading: creditsLoading } = useCredits();
  const { data: evalActivity, isLoading: activityLoading } = useEvalActivity();
  const { data: scoreDistribution, isLoading: distLoading } = useScoreDistribution();

  const firstName = profile?.full_name?.split(' ')[0] ?? 'there';

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
            Welcome back, {firstName}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Here&apos;s an overview of your research activity.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="sm:size-default" onClick={() => navigate('/sources')}>
            <Plus className="mr-2 h-4 w-4" />
            Upload Sources
          </Button>
          <Button size="sm" className="sm:size-default" onClick={() => navigate('/evaluate')}>
            <Target className="mr-2 h-4 w-4" />
            New Evaluation
          </Button>
        </div>
      </div>

      {/* Credit Warning */}
      <CreditWarning />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Sources"
          value={stats?.totalSources ?? 0}
          icon={FileText}
          color="indigo"
          loading={statsLoading}
          subtitle="Documents uploaded"
        />
        <StatCard
          title="Folders"
          value={stats?.totalFolders ?? 0}
          icon={FolderOpen}
          color="slate"
          loading={statsLoading}
          subtitle="Organized collections"
        />
        <StatCard
          title="Evaluations"
          value={stats?.completedEvals ?? 0}
          icon={BarChart3}
          color="emerald"
          loading={statsLoading}
          subtitle={`${stats?.totalEvaluations ?? 0} total, avg ${(stats?.avgScore ?? 0).toFixed(0)}%`}
        />
        <StatCard
          title="Credits"
          value={credits ?? 0}
          icon={Coins}
          color="amber"
          loading={creditsLoading}
          subtitle="Available balance"
        />
      </div>

      {/* Charts Row */}
      {activityLoading && distLoading ? (
        <DashboardChartsSkeleton />
      ) : (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Evaluation Activity Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Evaluation Activity</CardTitle>
            <span className="text-xs text-slate-500">Last 30 days</span>
          </CardHeader>
          <CardContent>
            {activityLoading ? (
              <ChartSkeleton height={250} />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={evalActivity ?? []}>
                  <defs>
                    <linearGradient id="evalGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      fontSize: '13px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#4f46e5"
                    strokeWidth={2}
                    fill="url(#evalGradient)"
                    name="Evaluations"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Score Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Score Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {distLoading ? (
              <ChartSkeleton height={250} />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={scoreDistribution ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="range"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      fontSize: '13px',
                    }}
                  />
                  <Bar
                    dataKey="count"
                    fill="#4f46e5"
                    radius={[4, 4, 0, 0]}
                    name="Sources"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Bottom Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Evaluations */}
        <RecentEvals />

        {/* Audit Trail */}
        <AuditTrail limit={15} />
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <button
              onClick={() => navigate('/sources')}
              className="flex items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-slate-50"
            >
              <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
                <FileText className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Upload Sources</p>
                <p className="text-xs text-slate-500">PDF, DOCX, TXT</p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </button>

            <button
              onClick={() => navigate('/criteria')}
              className="flex items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-slate-50"
            >
              <div className="rounded-lg bg-emerald-50 p-2 text-emerald-600">
                <Target className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Define Criteria</p>
                <p className="text-xs text-slate-500">With weights</p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </button>

            <button
              onClick={() => navigate('/search')}
              className="flex items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-slate-50"
            >
              <div className="rounded-lg bg-amber-50 p-2 text-amber-600">
                <Search className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Search Papers</p>
                <p className="text-xs text-slate-500">Semantic Scholar</p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </button>

            <button
              onClick={() => navigate('/reports')}
              className="flex items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-slate-50"
            >
              <div className="rounded-lg bg-rose-50 p-2 text-rose-600">
                <BarChart3 className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Reports</p>
                <p className="text-xs text-slate-500">PRISMA & reviews</p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
